<script setup lang="ts">
import semverGt from "semver/functions/gt"
import { defineAsyncComponent, ref, onMounted, watch } from "vue"
import { useGlobalStore } from "@/stores/global"
import { useUiStore } from "@/stores/ui"
import { useCurrentAttackerStore } from "@/stores/currentAttacker"
import { useCurrentDefenderStore } from "@/stores/currentDefender"
import { usePageCrunchSingleStore } from "@/stores/pageCrunchSingle"
import { useUpdateManagementStore } from "@/stores/updateManagement"

const Updating = defineAsyncComponent(
  () => import("@/components/Init/Updating.vue")
)

const globalStore = useGlobalStore()
const uiStore = useUiStore()
const currentAttackerStore = useCurrentAttackerStore()
const currentDefenderStore = useCurrentDefenderStore()
const storePageCrunchSingle = usePageCrunchSingleStore()
const updateManagementStore = useUpdateManagementStore()

// Get runtime config (auto-imported in Nuxt)
const runtimeConfig = useRuntimeConfig()

// Use ref for client-side only state
const isInitialized = ref<boolean>(false)

function init(): void {
  // Close app nav drawer if open.
  uiStore.closeAppNavDrawer()
  // Load test profiles if testing.
  if (runtimeConfig.public.seedTestData) {
    // Intro message was skipped so set this to false here.
    globalStore.newUser = false

    // Check if we're in a Cypress test with logged-out mode
    const isCypressLoggedOutTest =
      (window as Window & { Cypress?: { env: (key: string) => string } })
        .Cypress &&
      (
        window as Window & { Cypress?: { env: (key: string) => string } }
      ).Cypress.env("USER_AUTH") === "logged-out"

    // Load test profiles for logged-out Cypress tests
    if (isCypressLoggedOutTest && globalStore.profiles.length === 0) {
      // Dynamic import to only load test profiles when needed
      import("~/data/profiles/profilesTest").then((module) => {
        globalStore.loadProfiles(module.default)
      })
    }
    storePageCrunchSingle.panelsCrunchSingle = [0, 1]
  } else {
    // Initialise profile data if profiles object is missing
    // (local storage was deleted by another means).
    if (typeof globalStore.profiles === "undefined") {
      globalStore.initialiseProfileData()
    }
    if (globalStore.newUser) {
      // Only load profiles if none exist.
      if (globalStore.profiles.length === 0) {
        // Dynamic import to only load demo profiles when needed
        import("~/data/profiles/profilesDemo").then((module) => {
          globalStore.loadProfiles(module.default)
        })
      }
      // New users get the adhoc profile UI by default.
      globalStore.setProfileUi({
        profileType: "attacker",
        profileUiType: "adhoc",
      })
      globalStore.setProfileUi({
        profileType: "defender",
        profileUiType: "adhoc",
      })
    }
    if (currentAttackerStore.currentAttacker === null) {
      currentAttackerStore.setCurrentAttacker(null)
    }
    if (currentDefenderStore.currentDefender === null) {
      currentDefenderStore.setCurrentDefender(null)
    }
    globalStore.readOnlyProfiles = false
  }
  globalStore.newUser = false
}

// Move initialization to onMounted to avoid hydration mismatch
onMounted(() => {
  try {
    init()

    const envPackageVersion: string = runtimeConfig.public.clientVersion
    const runningLatestVersion: boolean =
      globalStore.appVersionLastSeen === envPackageVersion
    const latestSuffix: string = runningLatestVersion ? "(latest)" : ""

    console.info(
      "Last seen version:",
      globalStore.appVersionLastSeen,
      latestSuffix
    )
    if (!runningLatestVersion) {
      console.info("Latest version:", envPackageVersion)
    }

    if (
      globalStore.appVersionLastSeen !== "0.0.0" &&
      semverGt(envPackageVersion, globalStore.appVersionLastSeen)
    ) {
      updateManagementStore.updateInProgress = true
    } else {
      // This is handled by RunUpdates.vue if this code isn't reached.
      // Make sure profiles are initialized before updating
      if (globalStore.profiles && Array.isArray(globalStore.profiles)) {
        globalStore.updateCurrentProfiles()
        globalStore.syncAppVersions()
        updateManagementStore.updateLastSeen()
      } else {
        console.warn("Profiles not properly initialized, skipping update")
      }
    }
  } catch (error) {
    console.error("Error during initialization:", error)
  } finally {
    isInitialized.value = true
  }
})

// Watch for updateInProgress changes and sync between stores
watch(
  () => updateManagementStore.updateInProgress,
  (newValue) => {
    if (newValue) {
      globalStore.updateInProgress = true
    }
  }
)
</script>

<template>
  <div>
    <ClientOnly>
      <Updating
        v-if="
          isInitialized &&
          (globalStore.updateInProgress ||
            updateManagementStore.updateInProgress)
        "
      />
    </ClientOnly>
  </div>
</template>
